import { CCAMDto, NGAPDto, PrecotationDto } from '@/services/dtos'
import CcamAndNgapSearchComponent from '@components/billing/CcamAndNgapSearchComponent'
import { PopConfirm } from '@components/common/PopConfirm'
import { useAppDispatch } from '@hooks/reduxHooks'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  Button,
  Dialog,
  DialogContent,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Box,
  Stack,
  Typography,
  Divider,
} from '@mui/material'
import { Doctor, MedicalEquipment, MedicalOrder } from '@services/api'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { removeUnfinishedPrecot } from '@state/reducers/billingReducer'
import { doRemoveMedicalOrder } from '@state/reducers/orderReducer'
import { codeEntentePrealable } from '@utils/constants'
import {
  CalendarDays,
  ChevronDown,
  ChevronRight,
  CircleUserRound,
  Coins,
  FileText,
  Handshake,
  Plus,
  Stethoscope,
  Trash2,
} from 'lucide-react'
import moment from 'moment'
import React, { useRef } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import MemberDoctorList from './MemberDoctorList'
import { Edit, InfoOutlined } from '@mui/icons-material'
import { CcamDetailsTooltip } from './CcamDetailsTooltip'

interface QuotationTableProps {
  orderId?: number
  rows: PrecotationDto[]
  onAddRowForMedicalOrder?: (medicalOrderId: number) => void
  medicalOrderId?: number
  isLoading: boolean
  registeredAt?: Date
  onUpdateRow?: (updatedRow: PrecotationDto) => void
  onRemoveRow?: (updatedRow: PrecotationDto) => void | Promise<void>
  medicalOrders?: MedicalOrder[]
  onUpdateMedicalOrderEquipment?: (
    medicalOrderIds: number[],
    medicalEquipmentId: number | null,
  ) => void
  onUpdateMedicalOrderAttendingDoctor?: (
    medicalOrderIds: number[],
    attendingDoctorId: number | null,
  ) => void
  localRowsRef?: React.MutableRefObject<PrecotationDto[]>
  saveCounter?: number
}
const inputClassName =
  'w-full px-2 py-0.5 text-xs rounded border-gray-300 border focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all duration-200 bg-white'
const selectClassName =
  'w-full px-2 py-0.5 text-xs font-semibold text-green-700 rounded border-green-200 border focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all duration-200 bg-green-50'
const modifierSelectSx = {
  minWidth: '56px',
  '& .MuiOutlinedInput-root': { borderRadius: '4px', fontSize: '0.75rem' },
  '& .MuiSelect-select': { py: 0.5, px: 1 },
} as const
const medicalEquipmentSelectSx = {
  minWidth: 220,
  fontSize: '0.8125rem',
  fontWeight: 500,
  '& .MuiSelect-select': { py: 0.5, px: 1 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0,0,0,0.23)',
  },
} as const

const stripTreeFields = (row: any): PrecotationDto => {
  const clean = { ...row }
  delete clean.hierarchy
  delete clean.isGroup
  delete clean.exam_code
  delete clean.exam_label
  delete clean.precotationCount
  delete clean.medicalOrderIds
  delete clean.medicalEquipmentId
  delete clean.medicalEquipments
  delete clean.attendingDoctor
  return clean as PrecotationDto
}

interface QuotationGroup {
  id: string
  examCode: string
  examLabel: string
  precotations: PrecotationDto[]
  medicalOrderIds: number[]
  medicalEquipmentId: number | null
  medicalEquipments: MedicalEquipment[]
  attendingDoctor: Doctor | null
}

interface GroupTheme {
  text: string
  ring: string
  soft: string
  border: string
}

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-[10px] font-semibold uppercase tracking-normal text-gray-500">
    {children}
  </span>
)

const groupThemes: GroupTheme[] = [
  {
    text: 'text-purple-700',
    ring: 'border-purple-200 text-purple-700',
    soft: 'bg-purple-50',
    border: 'border-purple-200',
  },
  {
    text: 'text-blue-600',
    ring: 'border-blue-200 text-blue-600',
    soft: 'bg-blue-50',
    border: 'border-blue-200',
  },
  {
    text: 'text-emerald-600',
    ring: 'border-emerald-200 text-emerald-600',
    soft: 'bg-emerald-50',
    border: 'border-emerald-200',
  },
]

const formatMoney = (value: number | string | undefined) => {
  const numberValue =
    typeof value === 'number' ? value : parseFloat(String(value ?? 0))
  const formatted = Number.isFinite(numberValue)
    ? numberValue.toLocaleString('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : '0,00'
  return `${formatted} €`
}

const getRowKey = (row: PrecotationDto, fallback: string) =>
  row.id ?? row.identifier ?? fallback

const isSamePrecotation = (a: PrecotationDto, b: PrecotationDto) => {
  if (a.id && b.id) return a.id === b.id
  if (a.identifier && b.identifier) return a.identifier === b.identifier
  return a === b
}

const hasEntente = (row: PrecotationDto) =>
  !!row.code_entente_prealable && row.code_entente_prealable !== '0'

const SummaryMetric: React.FC<{
  icon: React.ReactNode
  label: string
  value: React.ReactNode
}> = ({ icon, label, value }) => (
  <div className="flex min-w-0 items-center gap-3">
    <div className="text-gray-500">{icon}</div>
    <div className="min-w-0">
      <div className="text-xs font-semibold text-gray-500">{label}</div>
      <div className="truncate text-sm font-semibold text-gray-900">
        {value}
      </div>
    </div>
  </div>
)

const MedicalEquipmentCell = React.memo<{
  medicalEquipmentId: number | null
  medicalOrderIds: number[]
  equipments: MedicalEquipment[]
  onUpdate: (medicalOrderIds: number[], equipmentId: number | null) => void
}>(({ medicalEquipmentId, medicalOrderIds, equipments, onUpdate }) => {
  return (
    <Select
      size="small"
      fullWidth
      displayEmpty
      value={medicalEquipmentId ?? ''}
      onChange={(e) => {
        const val = e.target.value
        onUpdate(medicalOrderIds, val === '' ? null : Number(val))
      }}
      sx={medicalEquipmentSelectSx}
    >
      <MenuItem value="">
        <em>Aucun</em>
      </MenuItem>
      {equipments.map((eq) => (
        <MenuItem key={eq.id} value={eq.id}>
          {eq.code} — {eq.manufacturer}
          {eq.model ? ` (${eq.model})` : ''}
        </MenuItem>
      ))}
    </Select>
  )
})

const AttendingDoctorCell = React.memo<{
  attendingDoctor: Doctor | null | undefined
  medicalOrderIds: number[]
  onAdd: (medicalOrderIds: number[]) => void
  accent?: 'purple' | 'blue' | 'emerald'
}>(({ attendingDoctor, medicalOrderIds, onAdd }) => {
  if (attendingDoctor?.rppsNumber) {
    const initial = attendingDoctor.firstName?.charAt(0).toUpperCase() ?? ''
    const lastName = attendingDoctor.lastName ?? ''
    return (
      <div className="flex items-center gap-1.5 text-xs">
        <Stethoscope size={15} className="text-sky-500" />
        <span className="font-medium">
          Dr {initial}.{lastName}
        </span>
        <Button
          size="small"
          startIcon={<Edit fontSize="small" />}
          onClick={() => onAdd(medicalOrderIds)}
        ></Button>
      </div>
    )
  }
  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<AddCircleIcon fontSize="small" />}
      onClick={() => onAdd(medicalOrderIds)}
      sx={{
        borderRadius: '5px',
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.75rem',
        py: 0.25,
      }}
    >
      Ajouter
    </Button>
  )
})

const NumberInputCell = React.memo<{
  value: number | string
  row: PrecotationDto
  field: string
  type?: string
  onUpdate: (row: PrecotationDto) => void
}>(({ value, row, field, type, onUpdate }) => {
  const [local, setLocal] = useState(value ?? '')

  useEffect(() => {
    setLocal(value ?? '')
  }, [value])

  return (
    <input
      type={type ? type : 'number'}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() =>
        onUpdate({ ...row, [field]: type === 'date' ? local : Number(local) })
      }
      className={inputClassName}
    />
  )
})

const EntentePrealableCell = React.memo<{
  value: string
  row: PrecotationDto
  onUpdate: (row: PrecotationDto) => void
}>(({ value, row, onUpdate }) => {
  const [local, setLocal] = useState(value || '')

  useEffect(() => {
    setLocal(value || '')
  }, [value])

  return (
    <select
      value={local}
      onChange={(e) => {
        setLocal(e.target.value)
        onUpdate({ ...row, code_entente_prealable: e.target.value })
      }}
      className={selectClassName}
    >
      {codeEntentePrealable.map((code) => (
        <option key={code.value} value={code.value}>
          {code.label}
        </option>
      ))}
    </select>
  )
})

const ModifierCell = React.memo<{
  value: string | null
  row: PrecotationDto
  field: string
  onUpdate: (row: PrecotationDto) => void
}>(({ value, row, field, onUpdate }) => {
  if (row.cotation_type === 'NGAP') return null
  const options = ['', ...(row.modificateur?.split('') || [])]
  return (
    <Select
      size="small"
      fullWidth
      value={value || ''}
      onChange={(e) => onUpdate({ ...row, [field]: e.target.value || null })}
      sx={modifierSelectSx} // ← hoisted constant, see #13
    >
      {options.map((opt) => (
        <MenuItem key={opt} value={opt}>
          {opt === '' ? 'Aucun' : opt}
        </MenuItem>
      ))}
    </Select>
  )
})

const ActGridCell: React.FC<{
  children: React.ReactNode
  className?: string
}> = ({ children, className = '' }) => (
  <div className={`flex min-h-[38px] items-center px-3 ${className}`}>
    {children}
  </div>
)

const ModifierGridCell: React.FC<{
  field: 'm1' | 'm2' | 'm3' | 'm4'
  row: PrecotationDto
  onUpdate: (row: PrecotationDto) => void
}> = ({ field, row, onUpdate }) => {
  if (row.cotation_type === 'NGAP') {
    return <span className="text-gray-700">-</span>
  }

  return (
    <ModifierCell
      value={row[field] ?? null}
      row={row}
      field={field}
      onUpdate={onUpdate}
    />
  )
}

const QuotationActGridRow: React.FC<{
  row: PrecotationDto
  onUpdate: (row: PrecotationDto) => void
  onRemove: (row: PrecotationDto) => void
  ccamCache: Map<string, CCAMDto>
  examDate: Date | string
}> = React.memo(({ row, onUpdate, onRemove, ccamCache, examDate }) => {
  const handleCotationChange = useCallback(
    (v: { cotationType: 'NGAP' | 'CCAM'; value: CCAMDto | NGAPDto }) => {
      let newRow = { ...row }
      if (v.cotationType === 'CCAM') {
        const val = v.value as CCAMDto
        newRow = {
          ...newRow,
          cotation: val.code,
          prix_unitaire: val.prixNonOptam,
          modificateur: val.modificateurs || '',
          cotation_type: 'CCAM',
        }
      } else {
        const val = v.value as NGAPDto
        newRow = {
          ...newRow,
          cotation: val.code,
          prix_unitaire: val.prix,
          cotation_type: 'NGAP',
          modificateur: '',
          m1: '',
          m2: '',
          m3: '',
          m4: '',
        }
      }

      onUpdate(newRow)
    },
    [onUpdate, row],
  )

  const isFt = row.cotation_type === 'FT'
  const isNgap = row.cotation_type === 'NGAP'
  return (
    <div className="grid min-w-[1128px] grid-cols-[220px_72px_72px_72px_72px_125px_175px_150px_130px_40px] items-center border-t border-gray-200 bg-white text-xs first:border-t-0">
      <ActGridCell>
        <div className="flex min-w-0 items-center gap-1.5 overflow-hidden">
          <Tooltip title={row.cotation || ''} arrow placement="top">
            <span className="truncate text-sm font-semibold text-gray-900">
              {row.cotation || 'Aucun'}
            </span>
          </Tooltip>
          <span className="shrink-0 rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs font-medium text-gray-600">
            {row.cotation_type || '-'}
          </span>
          {row.cotation_type === 'CCAM' && row.cotation && (
            <CcamDetailsTooltip
              code={row.cotation}
              examDate={examDate as Date}
              cache={ccamCache}
            />
          )}
          {!isFt && (
            <CcamAndNgapSearchComponent onChange={handleCotationChange} />
          )}
        </div>
      </ActGridCell>
      <ActGridCell>
        <ModifierGridCell field="m1" row={row} onUpdate={onUpdate} />
      </ActGridCell>
      <ActGridCell>
        <ModifierGridCell field="m2" row={row} onUpdate={onUpdate} />
      </ActGridCell>
      <ActGridCell>
        <ModifierGridCell field="m3" row={row} onUpdate={onUpdate} />
      </ActGridCell>
      <ActGridCell>
        <ModifierGridCell field="m4" row={row} onUpdate={onUpdate} />
      </ActGridCell>
      <ActGridCell>
        {isNgap || isFt ? (
          <span className="text-gray-700">-</span>
        ) : (
          <NumberInputCell
            value={row.association ?? ''}
            row={row}
            field="association"
            onUpdate={onUpdate}
          />
        )}
      </ActGridCell>
      <ActGridCell>
        {!isFt && (
          <EntentePrealableCell
            value={row.code_entente_prealable ?? ''}
            row={row}
            onUpdate={onUpdate}
          />
        )}
      </ActGridCell>
      <ActGridCell>
        {isFt || !hasEntente(row) ? (
          <span className="font-medium text-gray-400">Non requise</span>
        ) : (
          <NumberInputCell
            type="date"
            value={row.date_entente_prealable ?? ''}
            row={row}
            field="date_entente_prealable"
            onUpdate={onUpdate}
          />
        )}
      </ActGridCell>
      <ActGridCell className="justify-end">
        {isFt ? (
          <span className="font-semibold text-gray-900">
            {formatMoney(row.prix_unitaire)}
          </span>
        ) : (
          <div className="flex items-center gap-1">
            <NumberInputCell
              value={row.prix_unitaire}
              row={row}
              field="prix_unitaire"
              onUpdate={onUpdate}
            />
            <span className="font-semibold text-gray-700">€</span>
          </div>
        )}
      </ActGridCell>
      <ActGridCell className="justify-center">
        {!isFt && (
          <PopConfirm
            onConfirm={() => onRemove(row)}
            title="Supprimer l’acte"
            description="Êtes-vous sûr de vouloir supprimer cet acte ?"
          >
            <IconButton size="small" color="error">
              <Trash2 size={15} />
            </IconButton>
          </PopConfirm>
        )}
      </ActGridCell>
    </div>
  )
})

const QuotationTable: React.FC<QuotationTableProps> = ({
  orderId,
  rows,
  isLoading,
  registeredAt,
  onUpdateRow,
  onRemoveRow,
  onAddRowForMedicalOrder,
  medicalOrders = [],
  onUpdateMedicalOrderEquipment,
  onUpdateMedicalOrderAttendingDoctor,
  localRowsRef,
  saveCounter,
}) => {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const [isDeletePending, setIsDeletePending] = useState(false)
  const [localRows, setLocalRows] = useState<PrecotationDto[]>(rows)
  const [isDirty, setIsDirty] = useState(false)
  const prevSaveCounter = useRef(saveCounter)
  const [doctorSearchDialog, setDoctorSearchDialog] = useState<{
    open: boolean
    medicalOrderIds: number[]
  }>({ open: false, medicalOrderIds: [] })
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    () => new Set(),
  )
  const ccamCacheRef = useRef<Map<string, CCAMDto>>(new Map())

  useEffect(() => {
    if (localRowsRef) {
      localRowsRef.current = localRows.filter((r) => r.cotation_type !== 'FT')
    }
  }, [localRows, localRowsRef])

  const rowsRef = useRef(rows)
  useEffect(() => {
    if (rowsRef.current !== rows) {
      rowsRef.current = rows
      if (onUpdateRow || !isDirty) {
        setLocalRows(rows)
      } else {
        setLocalRows((prev) => {
          const prevKeys = new Set(
            prev.map((r) => String(r.id ?? r.identifier ?? '')),
          )
          const newRows = rows.filter(
            (r) => !prevKeys.has(String(r.id ?? r.identifier ?? '')),
          )
          if (newRows.length > 0) {
            return [...prev, ...newRows]
          }
          return prev
        })
      }
    }
  }, [rows, isDirty, onUpdateRow])

  useEffect(() => {
    if (saveCounter !== undefined && saveCounter !== prevSaveCounter.current) {
      prevSaveCounter.current = saveCounter
      setIsDirty(false)
      setLocalRows(rows)
      rowsRef.current = rows
    }
  }, [saveCounter, rows])

  const quotationGroups = useMemo<QuotationGroup[]>(() => {
    const sortPrecotations = (precotations: PrecotationDto[]) =>
      [...precotations].sort((a, b) => {
        if (a.cotation_type === 'FT' && b.cotation_type !== 'FT') return 1
        if (a.cotation_type !== 'FT' && b.cotation_type === 'FT') return -1
        return 0
      })

    if (!medicalOrders.length) {
      if (!localRows.length) return []

      return [
        {
          id: 'actes',
          examCode: 'actes',
          examLabel: 'Actes',
          precotations: sortPrecotations(localRows),
          medicalOrderIds: [],
          medicalEquipmentId: null,
          medicalEquipments: [],
          attendingDoctor: null,
        },
      ]
    }

    const rowsByMedicalOrder = new Map<number, PrecotationDto[]>()
    localRows.forEach((row) => {
      if (!row.id_medical_order) return

      const existing = rowsByMedicalOrder.get(row.id_medical_order) ?? []
      rowsByMedicalOrder.set(row.id_medical_order, [...existing, row])
    })

    const medicalOrderIds = new Set(medicalOrders.map((mo) => mo.id))
    const orphanRows = localRows.filter(
      (row) =>
        !row.id_medical_order || !medicalOrderIds.has(row.id_medical_order),
    )

    const groups = medicalOrders.map<QuotationGroup>((mo) => ({
      id: `medical-order-${mo.id}`,
      examCode: mo.exam.code,
      examLabel: mo.exam.label,
      precotations: sortPrecotations(rowsByMedicalOrder.get(mo.id) ?? []),
      medicalOrderIds: [mo.id],
      medicalEquipmentId: mo.medicalEquipmentId ?? null,
      medicalEquipments: mo.availableMedicalEquipments ?? [],
      attendingDoctor: mo.attendingDoctor ?? null,
    }))

    if (orphanRows.length) {
      groups.push({
        id: 'orphan-acts',
        examCode: 'orphan-acts',
        examLabel: 'Actes sans examen',
        precotations: sortPrecotations(orphanRows),
        medicalOrderIds: [],
        medicalEquipmentId: null,
        medicalEquipments: [],
        attendingDoctor: null,
      })
    }

    return groups
  }, [localRows, medicalOrders])

  const handleRemove = useCallback(
    async (cotation: PrecotationDto) => {
      const clean = stripTreeFields(cotation)
      setLocalRows((prev) =>
        prev.filter((row) => !isSamePrecotation(row, clean)),
      )
      if (!onRemoveRow) {
        setIsDirty(true)
      }
      if (onRemoveRow) {
        await onRemoveRow(clean)
      } else {
        dispatch(removeUnfinishedPrecot(clean))
      }
      dispatch(
        enqueueSnackbar({
          message: intl.formatMessage({ id: 'quotation-deleted-successfully' }),
          type: 'success',
        }),
      )
    },
    [onRemoveRow, dispatch, intl],
  )

  const handleUpdate = useCallback(
    (updatedRow: PrecotationDto) => {
      const clean = stripTreeFields(updatedRow)
      if (onUpdateRow) {
        onUpdateRow(clean)
      } else {
        setIsDirty(true)
      }
      setLocalRows((prev) =>
        prev.map((r) =>
          (r.id && r.id === clean.id) ||
          (r.identifier && r.identifier === clean.identifier)
            ? clean
            : r,
        ),
      )
    },
    [onUpdateRow],
  )

  const handleUpdateMedicalEquipment = useCallback(
    (medicalOrderIds: number[], medicalEquipmentId: number | null) => {
      if (onUpdateMedicalOrderEquipment) {
        onUpdateMedicalOrderEquipment(medicalOrderIds, medicalEquipmentId)
      }
    },
    [onUpdateMedicalOrderEquipment],
  )

  const handleUpdateMedicalOrderAttendingDoctor = useCallback(
    (medicalOrderIds: number[], attendingDoctorId: number | null) => {
      if (onUpdateMedicalOrderAttendingDoctor) {
        onUpdateMedicalOrderAttendingDoctor(medicalOrderIds, attendingDoctorId)
      }
    },
    [onUpdateMedicalOrderAttendingDoctor],
  )

  const handleRemoveMedicalOrder = useCallback(
    async (medicalOrderId: number) => {
      if (orderId) {
        setIsDeletePending(true)
        try {
          // First, remove all precotations associated with this medical order
          const precotationsToRemove = localRows.filter(
            (row) => row.id_medical_order === medicalOrderId,
          )

          await Promise.all(
            precotationsToRemove.map((precotation) =>
              handleRemove(precotation),
            ),
          )

          // Then remove the medical order
          await dispatch(
            doRemoveMedicalOrder({
              orderId: orderId,
              medicalOrderId: medicalOrderId,
            }),
          ).unwrap()

          dispatch(
            enqueueSnackbar({
              message: intl.formatMessage({
                id: 'medical-order-deleted-successfully',
              }),
              type: 'success',
            }),
          )
        } catch (error) {
          console.error('Error removing medical order:', error)
          dispatch(
            enqueueSnackbar({
              message: intl.formatMessage({
                id: 'error-removing-medical-order',
              }),
              type: 'error',
            }),
          )
        } finally {
          setIsDeletePending(false)
        }
      }
    },
    [orderId, localRows, handleRemove, dispatch, intl],
  )

  const toggleGroup = useCallback((groupId: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }, [])

  return (
    <div className="relative flex min-h-[400px] min-w-0 flex-1 flex-col overflow-hidden bg-slate-50">
      {isLoading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70">
          <CircularProgress size={28} />
        </div>
      )}
      <div className="flex-1 overflow-auto p-3">
        {quotationGroups.length === 0 ? (
          <div className="flex min-h-[220px] items-center justify-center rounded border border-dashed border-gray-300 bg-white text-sm text-gray-500">
            Aucun acte à afficher
          </div>
        ) : (
          <div className="space-y-2.5">
            {quotationGroups.map((group, groupIndex) => {
              const medicalOrderId = group.medicalOrderIds[0]
              const theme = groupThemes[groupIndex % groupThemes.length]
              const isCollapsed = collapsedGroups.has(group.id)
              const count = group.precotations.filter(
                (p) => p.cotation_type !== 'FT',
              ).length
              const groupTotal = group.precotations.reduce((acc, row) => {
                const value =
                  typeof row.prix_unitaire === 'number'
                    ? row.prix_unitaire
                    : parseFloat(String(row.prix_unitaire ?? 0))
                return acc + (Number.isFinite(value) ? value : 0)
              }, 0)
              const firstDate =
                group.precotations.find((row) => row.date_execution)
                  ?.date_execution ?? registeredAt
              const formattedDate = firstDate
                ? moment(firstDate).format('DD/MM/YYYY')
                : '-'
              const groupHasEntente = group.precotations.some(hasEntente)

              return (
                <section
                  key={group.id}
                  className={`overflow-hidden rounded-md border bg-white shadow-sm ${theme.border}`}
                >
                  <div className="grid gap-3 border-b border-gray-200 px-4 py-3 xl:grid-cols-[minmax(300px,1.4fr)_minmax(230px,1fr)_minmax(220px,1fr)_48px] xl:items-center">
                    <div className="flex min-w-0 items-center gap-3">
                      <button
                        type="button"
                        onClick={() => toggleGroup(group.id)}
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md border ${theme.ring} ${theme.soft}`}
                        aria-label={
                          isCollapsed ? 'Afficher l’examen' : 'Masquer l’examen'
                        }
                      >
                        {isCollapsed ? (
                          <ChevronRight size={18} />
                        ) : (
                          <ChevronDown size={18} />
                        )}
                      </button>
                      <div className="min-w-0">
                        <div
                          className={`truncate text-base font-bold ${theme.text}`}
                        >
                          {group.medicalOrderIds.length === 0
                            ? group.examLabel
                            : `${group.examCode} (${count})`}
                        </div>
                        {group.medicalOrderIds.length > 0 && (
                          <div
                            className="mt-0.5 truncate text-sm font-medium text-gray-600"
                            title={group.examLabel}
                          >
                            {group.examLabel}
                          </div>
                        )}
                      </div>
                    </div>
                    {group.medicalOrderIds.length > 0 && (
                      <>
                        <div className="flex min-w-0 items-center gap-3">
                          <div
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md border ${theme.ring} ${theme.soft}`}
                          >
                            <CircleUserRound size={17} />
                          </div>
                          <div className="min-w-0">
                            <FieldLabel>Modalité</FieldLabel>
                            <MedicalEquipmentCell
                              medicalEquipmentId={group.medicalEquipmentId}
                              medicalOrderIds={group.medicalOrderIds}
                              equipments={group.medicalEquipments ?? []}
                              onUpdate={handleUpdateMedicalEquipment}
                            />
                          </div>
                        </div>
                        <div className="min-w-0">
                          <FieldLabel>Médecin traitant</FieldLabel>
                          <div className="mt-1">
                            <AttendingDoctorCell
                              attendingDoctor={group.attendingDoctor}
                              medicalOrderIds={group.medicalOrderIds}
                              onAdd={(moIds) => {
                                setDoctorSearchDialog({
                                  open: true,
                                  medicalOrderIds: moIds,
                                })
                              }}
                            />
                          </div>
                        </div>
                      </>
                    )}
                    {medicalOrderId && (
                      <div className="flex justify-end">
                        <PopConfirm
                          onConfirm={() =>
                            handleRemoveMedicalOrder(medicalOrderId)
                          }
                          title={intl.formatMessage({
                            id: 'confirm-deletion',
                          })}
                          description={intl.formatMessage({
                            id: 'confirm-delete-medical-order',
                          })}
                          loading={isDeletePending}
                        >
                          <IconButton
                            color="error"
                            size="small"
                            disabled={isDeletePending}
                            sx={{
                              border: '1px solid #fecaca',
                              borderRadius: '6px',
                              height: 34,
                              width: 34,
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </PopConfirm>
                      </div>
                    )}
                  </div>
                  {!isCollapsed && (
                    <>
                      <div className="grid gap-4 border-b border-gray-200 px-10 py-3 md:grid-cols-2 xl:grid-cols-4">
                        <SummaryMetric
                          icon={<FileText size={22} />}
                          label="Nombre d’actes"
                          value={<span className={theme.text}>{count}</span>}
                        />
                        <SummaryMetric
                          icon={<CalendarDays size={22} />}
                          label="Date de soin"
                          value={formattedDate}
                        />
                        <SummaryMetric
                          icon={<Coins size={23} />}
                          label="Total prix"
                          value={
                            <span className={theme.text}>
                              {formatMoney(groupTotal)}
                            </span>
                          }
                        />
                        <SummaryMetric
                          icon={<Handshake size={23} />}
                          label="Entente"
                          value={
                            <span
                              className={
                                groupHasEntente
                                  ? 'rounded bg-amber-50 px-1.5 py-0.5 text-xs font-bold text-amber-700 ring-1 ring-amber-200'
                                  : 'rounded bg-green-50 px-1.5 py-0.5 text-xs font-bold text-green-700 ring-1 ring-green-200'
                              }
                            >
                              {groupHasEntente ? 'Entente' : 'Pas entente'}
                            </span>
                          }
                        />
                      </div>

                      <div className="overflow-x-auto px-3 py-3">
                        <div className="min-w-[1128px]">
                          <div className="grid grid-cols-[220px_72px_72px_72px_72px_125px_175px_150px_130px_40px] pb-2 text-[11px] font-bold uppercase tracking-normal text-gray-500">
                            <div className="px-3">Code Acte</div>
                            <div className="px-3">M1</div>
                            <div className="px-3">M2</div>
                            <div className="px-3">M3</div>
                            <div className="px-3">M4</div>
                            <div className="px-3">Association</div>
                            <div className="px-3">Entente Préalable</div>
                            <div className="px-3">Date Entente</div>
                            <div className="px-3 text-right">Prix Unitaire</div>
                            <div className="px-3" />
                          </div>
                          <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
                            {group.precotations.length === 0 ? (
                              <div className="px-3 py-3 text-xs text-gray-500">
                                Aucun acte ajouté pour cet examen.
                              </div>
                            ) : (
                              group.precotations.map((row, index) => (
                                <QuotationActGridRow
                                  key={getRowKey(
                                    row,
                                    `${group.id}-${row.cotation}-${index}`,
                                  )}
                                  row={row}
                                  onUpdate={handleUpdate}
                                  onRemove={handleRemove}
                                  ccamCache={ccamCacheRef.current}
                                  examDate={
                                    row.date_execution ??
                                    registeredAt ??
                                    new Date()
                                  }
                                />
                              ))
                            )}
                          </div>
                          {onAddRowForMedicalOrder && medicalOrderId && (
                            <button
                              type="button"
                              onClick={() =>
                                onAddRowForMedicalOrder(medicalOrderId)
                              }
                              className={`mt-2 flex h-8 w-full items-center justify-center gap-1.5 rounded border border-dashed ${theme.border} bg-white text-sm font-bold ${theme.text}`}
                            >
                              <Plus size={15} />
                              Ajouter un acte
                            </button>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </section>
              )
            })}
          </div>
        )}
      </div>
      <Dialog
        fullWidth
        maxWidth="md"
        open={doctorSearchDialog.open}
        onClose={() =>
          setDoctorSearchDialog({ open: false, medicalOrderIds: [] })
        }
      >
        <DialogContent sx={{ height: '50vh' }}>
          <MemberDoctorList
            onSelect={(member) => {
              handleUpdateMedicalOrderAttendingDoctor(
                doctorSearchDialog.medicalOrderIds,
                member.id,
              )
              setDoctorSearchDialog({ open: false, medicalOrderIds: [] })
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default QuotationTable
